import { useState, useRef } from "react";
import api from "@/lib/api";
import { UploadCloud, Loader2 } from "lucide-react";

const ALLOWED_EXTS = ["pdf", "doc", "docx", "jpg", "jpeg", "png"];
const MAX_BYTES = 50 * 1024 * 1024; // 50 MB

const ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png";

function extOf(name) {
  return (name.split(".").pop() || "").toLowerCase();
}
function isImage(ext) {
  return ["jpg", "jpeg", "png"].includes(ext);
}

/**
 * DocumentUploader
 *  - Drag & drop OR click to upload a document (PDF, DOC, DOCX, JPG, PNG) up to 50 MB
 *    to a private, authenticated Cloudinary folder for a specific client.
 *  - Calls onUploaded(doc) after the doc metadata is saved on our backend.
 *
 * Props:
 *   clientId?: string  // required for admin uploads; ignored for client (uses their own id)
 *   onUploaded: (doc) => void
 *   testid?: string
 */
export default function DocumentUploader({ clientId, onUploaded, testid = "doc" }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setError("");
    const ext = extOf(file.name);
    if (!ALLOWED_EXTS.includes(ext)) {
      return setError(`Allowed types: ${ALLOWED_EXTS.join(", ").toUpperCase()}`);
    }
    if (file.size > MAX_BYTES) {
      return setError("File exceeds the 50 MB limit.");
    }

    setBusy(true);
    setProgress(0);
    try {
      const params = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
      const { data: sig } = await api.get(`/documents/upload-signature${params}`);

      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);
      if (sig.type) form.append("type", sig.type);

      // Use Cloudinary's `auto` upload endpoint so PDFs/DOCs go to "raw"
      // and JPG/PNG go to "image" automatically.
      const url = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/auto/upload`;

      const res = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => {
          try {
            const parsed = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) resolve(parsed);
            else reject(new Error(parsed.error?.message || `Upload failed (${xhr.status})`));
          } catch (e) {
            reject(e);
          }
        };
        xhr.onerror = () => reject(new Error("Network error during upload"));
        xhr.send(form);
      });

      // Persist metadata on our backend
      const { data: doc } = await api.post("/documents", {
        client_id: sig.client_id,
        name: file.name,
        public_id: res.public_id,
        secure_url: res.secure_url,
        resource_type: res.resource_type, // "image" or "raw"
        file_format: ext,
        bytes: res.bytes ?? file.size,
      });
      onUploaded?.(doc);
      setBusy(false);
      setProgress(0);
    } catch (e) {
      setBusy(false);
      setError(e.response?.data?.detail || e.message || "Upload failed");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    upload(e.dataTransfer.files?.[0]);
  };

  return (
    <div>
      <label
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className="relative w-full rounded-lg border-2 border-dashed cursor-pointer transition-colors flex flex-col items-center justify-center text-center p-8"
        style={{
          borderColor: dragging ? 'var(--accent)' : 'rgba(255,255,255,0.12)',
          background: dragging ? 'rgba(212,163,115,0.06)' : 'rgba(11,19,43,0.4)',
        }}
        data-testid={`${testid}-dropzone`}
      >
        {busy ? (
          <>
            <Loader2 size={28} className="animate-spin mb-3" style={{ color: 'var(--accent)' }} />
            <div className="text-sm font-medium">Uploading… {progress}%</div>
            <div className="w-full max-w-[260px] h-1 mt-3 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <div className="h-full transition-all" style={{ width: `${progress}%`, background: 'var(--accent)' }} />
            </div>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3" style={{ background: 'rgba(212,163,115,0.12)' }}>
              <UploadCloud size={24} style={{ color: 'var(--accent)' }} />
            </div>
            <div className="font-medium text-sm">Drag &amp; drop a file</div>
            <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>or click to choose · PDF, DOC, DOCX, JPG, PNG · up to 50 MB</div>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="absolute inset-0 opacity-0 cursor-pointer"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`${testid}-input`}
        />
      </label>
      {error && <div className="text-xs mt-2" style={{ color: '#fca5a5' }} data-testid={`${testid}-error`}>{error}</div>}
    </div>
  );
}

export { isImage };
